import * as React from "react";
import styled from "styled-components";
const sanitizeHtml = require("sanitize-html");

import { Customer } from "../../../../domain/model/customer";
import { JobCareer } from "../../../../domain/model/job_career";
import { Occupation } from "../../../../domain/model/project";

import { Link } from "react-router-dom";
import { Multi_SkillTag } from "src/domain/model/skill_tag";
import {
  ConstBorderRound,
  ConstBreakPoint,
  ConstColor,
  ConstFontStyle,
} from "../../../../config/styles_const/styles_const";
import { SearchHighlight } from "../../../../domain/model/search_highlight";
import { extractOccupationsFromJobCareers } from "../../../../util/helper_job_career";
import AvatarMiddle from "../../atom/avatar/avatar_middle";
import BadgeBorder from "../../atom/badge/badge_border";
import HeadlineSmall from "../../atom/text/headline_small";
import ParagraphSup from "../../atom/text/paragraph_sup";

interface IProps {
  customer: Customer;
  jobCareers: JobCareer[];
  isBookmarked: boolean;
  cardURL: string;
  // trueの場合、カードクリックで新規タブで詳細を開く（一覧はそのまま残す）
  openInNewTab?: boolean;
}

const ScoutCustomerCard: React.FC<IProps> = (props) => {
  const { customer, jobCareers, isBookmarked, cardURL, openInNewTab } = props;

  const customerIconURL: string = customer.getIconURL();
  const customerCatchphrase: string = customer.getCatchphrase();
  const customerPr: string = customer.pr;
  const occupations: Occupation[] =
    extractOccupationsFromJobCareers(jobCareers);
  const isOccupations: boolean = occupations.length !== 0;
  const highlights = customer.highlights;
  // key名から該当highlightを抜き出せるようにhashをつくる
  const hashHighlights: { [key: string]: SearchHighlight } = {};
  highlights.map((highLight) => {
    hashHighlights[highLight.key] = highLight;
  });
  let customerCatchphraseInfo: string = "";
  let customerMainInfo: string = "";

  // sanitize用 emタグのみ許可しattributesは許可しない
  const defaultOptions = {
    allowedTags: ["em"],
    allowedAttributes: {},
  };

  let isSkillTag = false;

  if (
    customer.singleSkillTags.length !== 0 ||
    customer.multiSkillTags.length !== 0 ||
    customer.occupationSkillTags.length !== 0
  ) {
    isSkillTag = true;
  }

  if (highlights.length > 0) {
    // 検索結果のhighlightsにマッチしたkeyの配列をとる
    const highlightKeys: string[] = highlights.map((highLight) => {
      return highLight.key;
    });
    if (highlightKeys.some((key) => key === "catchphrase")) {
      const highlight = hashHighlights.catchphrase;
      customerCatchphraseInfo = sanitizeHtml(highlight.val, defaultOptions);
    }
    // マッチしたkeyから表示内容をつくる表示優先度はpr > dutySummary > requestedCondition > requirements > jobCareer.company > jobCareer.descriptions > 通常
    if (highlightKeys.some((key) => key === "jobCareers.description")) {
      const highlight = hashHighlights["jobCareers.description"];
      customerMainInfo = sanitizeHtml(highlight.val, defaultOptions);
    }
    if (highlightKeys.some((key) => key === "jobCareers.post")) {
      const highlight = hashHighlights["jobCareers.post"];
      customerMainInfo = sanitizeHtml(highlight.val, defaultOptions);
    }
    if (highlightKeys.some((key) => key === "jobCareers.companyName")) {
      const highlight = hashHighlights["jobCareers.companyName"];
      customerMainInfo = sanitizeHtml(highlight.val, defaultOptions);
    }
    if (highlightKeys.some((key) => key === "requirements")) {
      const highlight = hashHighlights.requirements;
      customerMainInfo = sanitizeHtml(highlight.val, defaultOptions);
    }
    if (highlightKeys.some((key) => key === "requestedCondition")) {
      const highlight = hashHighlights.requestedCondition;
      customerMainInfo = sanitizeHtml(highlight.val, defaultOptions);
    }
    if (highlightKeys.some((key) => key === "dutySummary")) {
      const highlight = hashHighlights.dutySummary;
      customerMainInfo = sanitizeHtml(highlight.val, defaultOptions);
    }
    if (highlightKeys.some((key) => key === "pr")) {
      const highlight = hashHighlights.pr;
      customerMainInfo = sanitizeHtml(highlight.val, defaultOptions);
    }
  }

  return (
    <Link
      to={cardURL}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noopener noreferrer" : undefined}
    >
      <ScoutCustomerCardWrapper>
        <Area area={"occupations"}>
          <div style={{ display: "flex" }}>
            {customer.displayOccupationSkillCategories.length > 0 && (
              <OccupationBadgeWrapper>
                <span
                  style={{
                    fontSize: 8,
                    fontWeight: "bold",
                    color: "#333",
                    marginRight: 6,
                  }}
                >
                  職種
                </span>
                {customer.displayOccupationSkillCategories
                  .slice(0, 3)
                  .map((val) => {
                    return (
                      <ScoutCustomerBadgeBorder key={val}>
                        {val}
                      </ScoutCustomerBadgeBorder>
                    );
                  })}
              </OccupationBadgeWrapper>
            )}

            {customer.multiSkillTags.filter(
              (v) =>
                v.category ===
                Multi_SkillTag.Category.Category_SpecialtyIndustry
            ).length > 0 && (
              <OccupationBadgeWrapper>
                <span
                  style={{
                    fontSize: 8,
                    fontWeight: "bold",
                    color: "#333",
                    marginRight: 6,
                    marginLeft: 35,
                  }}
                >
                  業界
                </span>
                {customer.multiSkillTags
                  .filter(
                    (v) =>
                      v.category ===
                      Multi_SkillTag.Category.Category_SpecialtyIndustry
                  )
                  .slice(0, 2)
                  .map((val) => {
                    return (
                      <ScoutCustomerBadgeBorder key={val.id}>
                        {val.name}
                      </ScoutCustomerBadgeBorder>
                    );
                  })}
              </OccupationBadgeWrapper>
            )}
          </div>
        </Area>
        <Area area={"avatar"}>
          <CustomerAvatar src={customerIconURL} />
        </Area>
        <Area area={"jobcareerCatchphrase"}>
          <ParagraphSup bold={true}>
            {customer.jobCareerCatchphrase}
          </ParagraphSup>
        </Area>
        <Area area={"age"}>
          {customer.getAgeString() && (
            <CustomerAge bold={true}>
              {"年代：" + customer.getAgeString()}
            </CustomerAge>
          )}
        </Area>
        <Area area={"scoutbookmark"}>
          <ScoutInfo>
            {isBookmarked && <FavoriteBadge bold={true} icon={"'\f004'"} />}
          </ScoutInfo>
        </Area>
        <Area area={"catchphrase"}>
          {customerCatchphraseInfo ? (
            <CustomerName
              dangerouslySetInnerHTML={{
                __html: customerCatchphraseInfo,
              }}
            />
          ) : (
            <CustomerName>{customerCatchphrase}</CustomerName>
          )}
        </Area>
        <Area area={"mainInfo"}>
          {customerMainInfo ? (
            <CustomerMainInfo
              dangerouslySetInnerHTML={{
                __html: customerMainInfo,
              }}
            />
          ) : (
            customerPr && <CustomerMainInfo>{customerPr}</CustomerMainInfo>
          )}
        </Area>
      </ScoutCustomerCardWrapper>
    </Link>
  );
};

export default ScoutCustomerCard;

const template = `
    "occupations occupations  occupations           occupations occupations  occupations"
    "avatar      ...          jobcareerCatchphrase  ...         age          scoutbookmark"
    "avatar      ...          catchphrase           ...         ...          ...         "
    "avatar      ...          ...                   ...         ...          ...         "
    "avatar      ...          mainInfo              mainInfo    mainInfo     mainInfo    "
`;

const templateSP = `
    "occupations occupations  occupations           occupations             occupations             occupations"
    "avatar      ...          jobcareerCatchphrase  jobcareerCatchphrase    jobcareerCatchphrase    jobcareerCatchphrase"
    "avatar      ...          catchphrase           catchphrase             catchphrase             catchphrase  "
    "avatar      ...          ...                   ...                     ...                     ...          "
    "avatar      ...          scoutbookmark         age                     ...                     address      "
    "avatar      ...          ...                   ...                     ...                     ...          "
    "mainInfo    mainInfo     mainInfo              mainInfo                mainInfo                mainInfo     "
`;

const ScoutCustomerCardWrapper = styled.div`
  border-radius: 10px;
  box-shadow: 0px 0px 10px rgba(199, 199, 199, 0.2);
  position: relative;
  padding: 20px;
  width: 100%;

  display: grid;
  grid-template-areas: ${templateSP};
  grid-template-columns: auto 20px auto 1fr auto auto;
  grid-template-rows: auto auto auto 10px auto 1fr auto 10px auto;
  grid-column-gap: 0;
  grid-row-gap: 0;
  justify-content: center;

  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    display: grid;
    grid-template-areas: ${template};
    grid-template-columns: auto 20px auto auto 1fr auto;
    grid-template-rows: auto auto auto 5px auto 5px 1fr;
    grid-column-gap: 0;
    grid-row-gap: 0;
    justify-content: center;
  }
`;

const Area = styled.div<{ area: string }>`
  grid-area: ${(props) => props.area};
  display: grid;
  align-items: center;
`;

const OccupationBadgeWrapper = styled.div`
  margin-bottom: 20px;
`;

const CustomerAvatar = styled(AvatarMiddle)``;

const CustomerName = styled(HeadlineSmall)``;

const CustomerAge = styled(ParagraphSup)`
  margin-left: auto;
  color: ${ConstColor.Gray};
  font-size: ${ConstFontStyle.LabelJob.fontSize};
`;

const CustomerMainInfo = styled(ParagraphSup)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-top: 10px;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    margin-top: 0;
  }
`;

const ScoutInfo = styled.div`
  display: flex;
  align-items: baseline;
  @media screen and (min-width: ${ConstBreakPoint.Default}) {
    margin-left: 20px;
  }
`;

const FavoriteBadge = styled(ParagraphSup)`
  color: ${ConstColor.Favorite};
`;

const ScoutCustomerBadgeBorder = styled(BadgeBorder)`
  color: #9a8c64;
  border: 1px solid #9a8c64;
  background-color: ${ConstColor.White};
  border-radius: ${ConstBorderRound.BadgeBorder};
`;



// WEBPACK FOOTER //
// ./src/presentation/common/organism/card/scout_customer_card.tsx